import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './pages/LandingPage.js';
import BookingForm from './pages/BookingForm';
import MyBookings from './pages/MyBookings.js';
import BookingsCalendar from './pages/BookingsCalendar.js';
import ServicesPage from './ServiceList';
import LoginPage from './pages/Login.js';
import AdminDashboard from './pages/AdminDashboard.js';
import WorkerDashboard from './pages/WorkerDashboard.js';
import OwnerDashboard from './pages/OwnerDashboard.js';
import OwnerCompanyPageEditor from './pages/OwnerCompanyPageEditor.js';
import OwnerInsightsGovernance from './pages/OwnerInsightsGovernance.js';
import OwnerApplicationPage from './pages/OwnerApplicationPage.js';
import WorkerApplicationPage from './pages/WorkerApplicationPage.js';
import CompanyPublicPage from './pages/CompanyPublicPage.js';
import SignUpClient from './pages/SignUpClient.js';
import SignUpOwner from './pages/SignUpOwner.js';
import SignUpWorker from './pages/SignUpWorker.js';
import WorkerAvailability from './pages/WorkerAvailability.js';
import WorkerServices from './pages/WorkerServices.js';
import UserProfile from './pages/UserProfile.js';
import Notification from './components/Notification';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!localStorage.getItem('token'));
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole'));
  const [notification, setNotification] = useState({ message: '', type: '' });

  useEffect(() => {
    const syncAuthState = () => {
      const token = localStorage.getItem('token');
      const role = localStorage.getItem('userRole');
      setIsAuthenticated(!!token);
      setUserRole(role);
    };

    syncAuthState();

    window.addEventListener('storage', syncAuthState);
    window.addEventListener('auth-changed', syncAuthState);

    return () => {
      window.removeEventListener('storage', syncAuthState);
      window.removeEventListener('auth-changed', syncAuthState);
    };
  }, []);

  const getDefaultRouteByRole = (role) => {
    if (role === 'admin') return '/dashboard';
    if (role === 'owner') return '/owner-dashboard';
    if (role === 'worker') return '/worker-dashboard';
    return '/';
  };

  const handleNotificationClose = () => {
    setNotification({ message: '', type: '' });
  };

  return (
    <Router>
      <div>
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={handleNotificationClose}
        />
        <Routes>
          <Route
            path="/"
            element={
              <LandingPage
                isAuthenticated={isAuthenticated}
                setIsAuthenticated={setIsAuthenticated}
                setUserRole={setUserRole}
                setNotification={setNotification}
              />
            }
          />
          <Route path="/myBookings" element={<MyBookings setNotification={setNotification} />} />
          <Route path="/mybookings" element={<MyBookings setNotification={setNotification} />} />
          <Route path="/my-bookings" element={<MyBookings setNotification={setNotification} />} />
          <Route
            path="/calendar"
            element={isAuthenticated ? <BookingsCalendar /> : <Navigate to="/login" replace />}
          />
          <Route path="/booking" element={<BookingForm setNotification={setNotification} />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route
            path="/my-services"
            element={
              isAuthenticated && ['worker', 'owner'].includes(userRole)
                ? <WorkerServices />
                : <Navigate to={isAuthenticated ? getDefaultRouteByRole(userRole) : '/login'} replace />
            }
          />
          <Route
            path="/worker-availability"
            element={
              isAuthenticated
                ? <WorkerAvailability />
                : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/worker-dashboard"
            element={
              isAuthenticated && ['worker', 'owner'].includes(userRole)
                ? (
                  <WorkerDashboard
                    onLogout={() => {
                      setIsAuthenticated(false);
                      setUserRole(null);
                    }}
                    setNotification={setNotification}
                  />
                )
                : <Navigate to={isAuthenticated ? getDefaultRouteByRole(userRole) : '/login'} replace />
            }
          />
          <Route
            path="/owner-dashboard"
            element={
              isAuthenticated && userRole === 'owner'
                ? (
                  <OwnerDashboard
                    onLogout={() => {
                      setIsAuthenticated(false);
                      setUserRole(null);
                    }}
                  />
                )
                : <Navigate to={isAuthenticated ? getDefaultRouteByRole(userRole) : '/login'} replace />
            }
          />
          <Route
            path="/owner-company-page"
            element={
              isAuthenticated && userRole === 'owner'
                ? <OwnerCompanyPageEditor />
                : <Navigate to={isAuthenticated ? getDefaultRouteByRole(userRole) : '/login'} replace />
            }
          />
          <Route
            path="/owner-insights"
            element={
              isAuthenticated && userRole === 'owner'
                ? <OwnerInsightsGovernance />
                : <Navigate to={isAuthenticated ? getDefaultRouteByRole(userRole) : '/login'} replace />
            }
          />
          <Route path="/companies/:slug" element={<CompanyPublicPage />} />
          <Route
            path="/login"
            element={
              isAuthenticated
                ? <Navigate to={getDefaultRouteByRole(userRole)} replace />
                : (
                  <LoginPage
                    setIsAuthenticated={setIsAuthenticated}
                    setUserRole={setUserRole}
                    setNotification={setNotification}
                  />
                )
            }
          />
          <Route
            path="/profile"
            element={
              isAuthenticated
                ? <UserProfile setNotification={setNotification} />
                : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/owner-application"
            element={
              isAuthenticated
                ? <OwnerApplicationPage />
                : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/worker-application"
            element={
              isAuthenticated
                ? <WorkerApplicationPage />
                : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/dashboard"
            element={
              isAuthenticated && userRole === 'admin'
                ? (
                  <AdminDashboard
                    onLogout={() => {
                      setIsAuthenticated(false);
                      setUserRole(null);
                    }}
                    setNotification={setNotification}
                  />
                )
                : <Navigate to={isAuthenticated ? getDefaultRouteByRole(userRole) : '/login'} replace />
            }
          />
          <Route path="/signup-client" element={<SignUpClient />} />
          <Route path="/signup-owner" element={<SignUpOwner />} />
          <Route path="/signup-worker" element={<SignUpWorker />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
